import {
    computeTaxFromMeta,
    computeTaxPartsFromMeta,
    parseMoney,
    partLineTaxAmount,
    partLineTaxBase,
    roundMoney,
    type TaxLineInfo,
} from '../workshop/jobEstimate/jobEstimateCalc';

export type FbrInvoiceLineAmountInput = {
    quantity?: unknown;
    unitPrice?: unknown;
    /** Discount percent (0-100). */
    discountRate?: unknown;
    /** Legacy additive multiplier — used only when no per-tax metadata is present. */
    taxRate?: unknown;
    /** Per-tax settings rows (same shape the vendor bill grid stores). */
    taxLineMeta?: unknown;
    fixedNotifiedApplicable?: unknown;
    mrpRateValue?: unknown;
};

/** Shared base derivation: qty/gross, discounted subtotal, FN-aware MRP and tax base seed. */
function deriveFbrLineBases(line: FbrInvoiceLineAmountInput) {
    const qty = parseMoney(line.quantity);
    const price = parseMoney(line.unitPrice);
    const gross = roundMoney(qty * price);
    const discPct = Math.max(0, Math.min(100, parseMoney(line.discountRate)));
    const subtotal = Math.max(0, roundMoney(gross * (1 - discPct / 100)));
    const fixedNotified =
        line.fixedNotifiedApplicable === true || line.fixedNotifiedApplicable === 'true';
    const mrp = fixedNotified ? parseMoney(line.mrpRateValue) : 0;
    const taxBase = partLineTaxBase(subtotal, qty, fixedNotified, mrp);
    const taxes = Array.isArray(line.taxLineMeta) ? (line.taxLineMeta as TaxLineInfo[]) : [];
    return { qty, gross, subtotal, mrp, taxBase, taxes };
}

/**
 * Same engine as vendor bills (computeTaxFromMeta): per-tax calculation base
 * (NetAmount | GrossAmount | MRP | Quantity), fixed amounts, tax-included
 * reverse extraction and base compounding via AffectBaseOfSubsequentTaxes.
 * The FBR Fixed Notified rule seeds the running base with MRP × qty so
 * 3rd-schedule lines are taxed on retail price; MRP is ignored when the FN
 * flag is off (mirrors the backend, which persists MrpRateValue = 0 then).
 */
export function computeFbrInvoiceLineAmounts(line: FbrInvoiceLineAmountInput) {
    const { qty, gross, subtotal, mrp, taxBase, taxes } = deriveFbrLineBases(line);
    const taxAmount =
        taxes.length > 0
            ? computeTaxFromMeta(taxes, subtotal, gross, qty, mrp)
            : (partLineTaxAmount(taxBase, parseMoney(line.taxRate)) ?? 0);
    return { gross, subtotal, taxAmount, total: roundMoney(subtotal + taxAmount) };
}

/**
 * Per-tax amounts for a line, aligned index-for-index with line.taxLineMeta.
 * Used by print/PDF per-rate breakdowns so each rate row shows the amount the
 * engine actually charged (compounding, gross/MRP bases, fixed, tax-included).
 */
export function computeFbrInvoiceLineTaxParts(line: FbrInvoiceLineAmountInput): number[] {
    const { qty, gross,subtotal, mrp, taxes } = deriveFbrLineBases(line);
    return computeTaxPartsFromMeta(taxes, subtotal, gross, qty, mrp);
}

export type FbrInvoiceTotals = {
    totalExTaxes: number;
    taxes: number;
    total: number;
};

export function computeFbrInvoiceTotals(
    lines: FbrInvoiceLineAmountInput[] | undefined,
    deliveryFees: number
): FbrInvoiceTotals {
    let ex = 0;
    let tx = 0;
    for (const line of lines ?? []) {
        const amounts = computeFbrInvoiceLineAmounts(line);
        ex += amounts.subtotal;
        tx += amounts.taxAmount;
    }
    return {
        totalExTaxes: roundMoney(ex),
        taxes: roundMoney(tx),
        total: roundMoney(ex + tx + (Number(deliveryFees) || 0)),
    };
}
